import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, Button, Tag, Space, Modal, Form, Input, Select, Progress,
  message, Popconfirm, Card, Row, Col, Statistic, DatePicker 
} from 'antd';
import { 
  PlusOutlined, PlayCircleOutlined, PauseCircleOutlined,
  CopyOutlined, DeleteOutlined, EyeOutlined, SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;
const { RangePicker } = DatePicker;

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [templates, setTemplates] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20
  });
  
  // 筛选条件
  const [filters, setFilters] = useState({
    account_id: '',
    campaign_id: '',
    search: ''
  });
  
  // 复制进度弹窗状态
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyProgress, setCopyProgress] = useState(0);
  const [copyStatus, setCopyStatus] = useState('');
  const [copyJobId, setCopyJobId] = useState('');
  const [copyTimer, setCopyTimer] = useState(null);
  const [issuesModalVisible, setIssuesModalVisible] = useState(false);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issueList, setIssueList] = useState([]);
  const [issueCampaign, setIssueCampaign] = useState(null);
  const [selectedIssueAdIds, setSelectedIssueAdIds] = useState([]);
  const [pageModalVisible, setPageModalVisible] = useState(false);
  const [pageList, setPageList] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [pageUpdateLoading, setPageUpdateLoading] = useState(false);
  const [batchUpdateResults, setBatchUpdateResults] = useState(null);

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchAdAccounts();
  }, []);

  const fetchCampaigns = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    activeFilters = filters
  ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('page_size', String(pageSize));
      const accountId = (activeFilters.account_id ?? '').toString().trim();
      const campaignId = (activeFilters.campaign_id ?? '').toString().trim();
      const search = (activeFilters.search ?? '').trim();
      if (accountId) params.append('account_id', accountId);
      if (campaignId) params.append('campaign_id', campaignId);
      if (search) params.append('search', search);

      const response = await api.get(`/campaigns/stats/?${params.toString()}`);
      setCampaigns(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      message.error('获取广告系列失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/campaign-templates/');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchAdAccounts = async () => {
    try {
      const response = await api.get('/ad-accounts/');
      setAdAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch ad accounts:', error);
    }
  };

  const handleCreate = async (values) => {
    try {
      await api.post('/campaigns/', values);
      message.success('创建成功');
      setModalVisible(false);
      form.resetFields();
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.detail || '创建失败');
    }
  };

  const handlePause = async (id) => {
    try {
      await api.post(`/campaigns/${id}/pause`);
      message.success('暂停任务已提交');
      fetchCampaigns();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleActivate = async (id) => {
    try {
      await api.post(`/campaigns/${id}/activate`);
      message.success('激活任务已提交');
      fetchCampaigns();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleCopy = async (record) => {
    try {
      // 显示进度弹窗
      setCopyModalVisible(true);
      setCopyProgress(0);
      setCopyStatus('正在提交任务...');
      
      // 确保campaign_id是字符串，避免JavaScript大整数精度问题
      const campaignIdStr = String(record.campaign_id);
      
      // 发起异步复制请求
      const response = await api.post(`/campaigns/${campaignIdStr}/deep-copy-async`, {
        name: record.name,
      });
      const jobId = response.data.job_id;
      setCopyJobId(jobId);
      setCopyStatus('任务已提交，正在处理...');
      
      // 轮询进度
      // const timer = setInterval(async () => {
      //   try {
      //     const statusResponse = await api.get(`/campaigns/deep-copy-status/${jobId}`);
      //     const { status, percent_completion, completed } = statusResponse.data;
          
      //     setCopyProgress(percent_completion);
      //     setCopyStatus(status);
          
      //     if (completed) {
      //       clearInterval(timer);
      //       setCopyStatus('复制完成！');
      //       setTimeout(() => {
      //         setCopyModalVisible(false);
      //         fetchCampaigns();
      //         message.success('深度复制成功！');
      //       }, 1500);
      //     }
      //   } catch (error) {
      //     clearInterval(timer);
      //     setCopyStatus('查询进度失败');
      //     message.error(error.response?.data?.detail || '复制失败');
      //   }
      // }, 2000);
      
      // setCopyTimer(timer);
      
    } catch (error) {
      setCopyModalVisible(false);
      message.error(error.response?.data?.detail || '复制失败');
    }
  };
  
  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (copyTimer) {
        clearInterval(copyTimer);
      }
    };
  }, [copyTimer]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/campaigns/${id}`);
      message.success('删除成功');
      fetchCampaigns();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCampaigns(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const emptyFilters = { account_id: '', campaign_id: '', search: '' };
    setFilters(emptyFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCampaigns(1, pagination.pageSize, emptyFilters);
  };

  const handleViewIssues = async (record) => {
    setIssueCampaign(record);
    setIssuesModalVisible(true);
    setIssuesLoading(true);
    setSelectedIssueAdIds([]);
    setBatchUpdateResults(null);
    try {
      const response = await api.get(
        `/campaigns/stats/${String(record.campaign_id)}/issues`
      );
      setIssueList(response.data.items || []);
    } catch (error) {
      message.error(error.response?.data?.detail || '获取错误广告失败');
      setIssueList([]);
    } finally {
      setIssuesLoading(false);
    }
  };

  const fetchPagesForIssueCampaign = async () => {
    if (!issueCampaign?.account_id) {
      message.error('无法获取广告账户 ID');
      return;
    }
    setPagesLoading(true);
    try {
      const response = await api.get(`/ad-accounts/${issueCampaign.account_id}/pages`);
      setPageList(response.data || []);
    } catch (error) {
      message.error(error.response?.data?.detail || '获取公共主页失败');
      setPageList([]);
    } finally {
      setPagesLoading(false);
    }
  };

  const handleOpenPageModal = async () => {
    if (!selectedIssueAdIds.length) {
      message.warning('请先选择要修改的广告');
      return;
    }
    setSelectedPageId(null);
    setBatchUpdateResults(null);
    setPageModalVisible(true);
    await fetchPagesForIssueCampaign();
  };

  const handleBatchUpdatePage = async () => {
    if (!selectedPageId) {
      message.warning('请选择公共主页');
      return;
    }
    if (!issueCampaign?.campaign_id) {
      message.error('系列信息缺失');
      return;
    }
    setPageUpdateLoading(true);
    try {
      const response = await api.post(
        `/campaigns/stats/${String(issueCampaign.campaign_id)}/issues/batch-update-page`,
        {
          ad_ids: selectedIssueAdIds,
          page_id: selectedPageId,
        }
      );
      setBatchUpdateResults(response.data);
      const { success_count, failed_count } = response.data;
      if (failed_count === 0) {
        message.success(`已成功处理 ${success_count} 条广告`);
      } else {
        message.warning(`成功 ${success_count} 条，失败 ${failed_count} 条`);
      }
      setSelectedIssueAdIds([]);
    } catch (error) {
      message.error(error.response?.data?.detail || '批量修改主页失败');
    } finally {
      setPageUpdateLoading(false);
    }
  };

  const columns = [
    {
      title: '系列ID',
      dataIndex: 'campaign_id',
      width: 120,
    },
    {
      title: '账户ID',
      dataIndex: 'account_id',
      width: 120,
    },
    {
      title: '系列名称',
      dataIndex: 'name',
      ellipsis: true,
      width: 200,
    },
    {
      title: '今日',
      key: 'today_stats',
      width: 150,
      render: (_, record) => (
        <div style={{ lineHeight: 1.7, fontSize: 12 }}>
          <div>今日消耗：${(record.today_spends || 0).toFixed(2)}</div>
          <div>今日订单金额：${(record.today_amount || 0).toFixed(2)}</div>
          <div>今日订单数：{(record.today_count ?? 0).toLocaleString()}</div>
        </div>
      ),
    },
    {
      title: '7天',
      key: 'seven_days_stats',
      width: 150,
      render: (_, record) => (
        <div style={{ lineHeight: 1.7, fontSize: 12 }}>
          <div>7天消耗：${(record.seven_days_spends || 0).toFixed(2)}</div>
          <div>7天订单金额：${(record.seven_days_amount || 0).toFixed(2)}</div>
          <div>7天ROI：{(record.seven_days_roi || 0).toFixed(2)}</div>
        </div>
      ),
    },
    {
      title: '总计',
      key: 'totals',
      width: 160,
      render: (_, record) => (
        <div style={{ lineHeight: 1.7, fontSize: 12 }}>
          <div>总订单数：{(record.purchase_counts ?? 0).toLocaleString()}</div>
          <div>总订单金额：${(record.purchase_amounts || 0).toFixed(2)}</div>
          <div>总充值用户数：{(record.purchase_users ?? 0).toLocaleString()}</div>
          <div>总消耗：${(record.spends || 0).toFixed(2)}</div>
          <div>总ROI：{(record.ROI || 0).toFixed(2)}</div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          'ACTIVE': { color: 'success', text: '活跃' },
          'PAUSED': { color: 'warning', text: '暂停' },
          'DELETED': { color: 'error', text: '删除' },
          'ARCHIVED': { color: 'default', text: '归档' },
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '广告错误',
      dataIndex: 'issue_count',
      width: 110,
      render: (count, record) => {
        if (!count) return <span style={{ color: '#999' }}>-</span>;
        return (
          <Button
            type="link"
            danger
            size="small"
            icon={<WarningOutlined />}
            style={{ padding: 0 }}
            onClick={() => handleViewIssues(record)}
          >
            {count} 个错误
          </Button>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_time',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button 
            icon={record.status === 'ACTIVE' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            size="small"
            onClick={() => record.status === 'ACTIVE' ? handlePause(record.campaign_id) : handleActivate(record.campaign_id)}
            type={record.status === 'ACTIVE' ? 'warning' : 'primary'}
          >
            {record.status === 'ACTIVE' ? '暂停' : '开启'}
          </Button>
          <Button 
            icon={<CopyOutlined />} 
            size="small"
            onClick={() => handleCopy(record)}
          >
            复制
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>广告系列管理</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => navigate('/campaigns/create')}
        >
          创建系列
        </Button>
      </div>

      {/* 筛选区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Form.Item label="搜索">
              <Input
                placeholder="系列名称 / 账户ID / 系列ID"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onPressEnter={handleSearch}
                prefix={<SearchOutlined />}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="账户ID">
              <Input
                placeholder="账户ID"
                value={filters.account_id}
                onChange={(e) => handleFilterChange('account_id', e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="系列ID">
              <Input
                placeholder="系列ID"
                value={filters.campaign_id}
                onChange={(e) => handleFilterChange('campaign_id', e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                查询
              </Button>
              <Button onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={campaigns}
        rowKey="campaign_id"
        loading={loading}
        scroll={{ x: 1600 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
            fetchCampaigns(page, pageSize, filters);
          },
          onShowSizeChange: (current, pageSize) => {
            setPagination({ current: 1, pageSize });
            fetchCampaigns(1, pageSize, filters);
          },
        }}
      />

      <Modal
        title="创建广告系列"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="name"
            label="系列名称"
            rules={[{ required: true, message: '请输入系列名称' }]}
          >
            <Input placeholder="输入系列名称" />
          </Form.Item>

          <Form.Item
            name="ad_account_id"
            label="广告账户"
            rules={[{ required: true, message: '请选择广告账户' }]}
          >
            <Select placeholder="选择广告账户">
              {adAccounts.map(acc => (
                <Option key={acc.id} value={acc.id}>{acc.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="template_id"
            label="系列模板"
            rules={[{ required: true, message: '请选择模板' }]}
          >
            <Select placeholder="选择模板">
              {templates.map(tpl => (
                <Option key={tpl.id} value={tpl.id}>{tpl.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="objective"
            label="广告目标"
            initialValue="OUTCOME_TRAFFIC"
          >
            <Select>
              <Option value="OUTCOME_TRAFFIC">流量</Option>
              <Option value="OUTCOME_SALES">销售</Option>
              <Option value="OUTCOME_AWARENESS">品牌认知</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="daily_budget"
            label="日预算 (USD)"
          >
            <Input type="number" placeholder="10.00" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 复制进度弹窗 */}
      <Modal
        title="深度复制进度"
        open={copyModalVisible}
        footer={null}
        closable={false}
        width={400}
      >
        <div style={{ padding: 20 }}>
          <div className="text-center mb-4">
            <p style={{ fontSize: 16, marginBottom: 16 }}>{copyStatus}</p>
            <Progress
              percent={copyProgress}
              status={copyProgress === 100 ? 'success' : 'active'}
              showInfo={true}
              strokeWidth={12}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title={
          issueCampaign
            ? `错误广告 - ${issueCampaign.name || issueCampaign.campaign_id}`
            : '错误广告'
        }
        open={issuesModalVisible}
        onCancel={() => {
          setIssuesModalVisible(false);
          setIssueList([]);
          setIssueCampaign(null);
          setSelectedIssueAdIds([]);
          setBatchUpdateResults(null);
        }}
        width={1100}
        footer={null}
      >
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#666' }}>
            已选 {selectedIssueAdIds.length} 条
            {issueCampaign?.account_id ? ` · 账户 ${issueCampaign.account_id}` : ''}
          </span>
          <Button
            type="primary"
            disabled={!selectedIssueAdIds.length}
            onClick={handleOpenPageModal}
          >
            批量修改主页
          </Button>
        </div>
        {batchUpdateResults && (
          <Card size="small" style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 8 }}>
              处理结果：成功 {batchUpdateResults.success_count} / 失败 {batchUpdateResults.failed_count}
            </div>
            <Table
              size="small"
              pagination={false}
              rowKey="ad_id"
              dataSource={batchUpdateResults.results || []}
              columns={[
                { title: '广告 ID', dataIndex: 'ad_id', width: 160 },
                { title: '广告名称', dataIndex: 'ad_name', width: 120, ellipsis: true },
                {
                  title: '状态',
                  key: 'status',
                  width: 90,
                  render: (_, record) => (
                    record.success
                      ? <Tag color="success">{record.skipped ? '已跳过' : '成功'}</Tag>
                      : <Tag color="error">失败</Tag>
                  ),
                },
                {
                  title: '新创意 ID',
                  dataIndex: 'new_creative_id',
                  width: 150,
                  render: (v) => v || '-',
                },
                {
                  title: '说明',
                  key: 'message',
                  ellipsis: true,
                  render: (_, record) => record.message || record.error_message || '-',
                },
              ]}
            />
          </Card>
        )}
        <Table
          loading={issuesLoading}
          dataSource={issueList}
          rowKey={(record) => record.ad_id || record.id}
          rowSelection={{
            selectedRowKeys: selectedIssueAdIds,
            onChange: (keys) => setSelectedIssueAdIds(keys),
            getCheckboxProps: (record) => ({
              disabled: !record.ad_id,
            }),
          }}
          pagination={false}
          size="small"
          scroll={{ x: 1200 }}
          columns={[
            {
              title: '广告 ID',
              dataIndex: 'ad_id',
              width: 160,
              render: (v) => v || '-',
            },
            {
              title: '广告名称',
              dataIndex: 'ad_name',
              width: 80,
              render: (v) => v || '-',
            },
            {
              title: '广告组',
              key: 'adset',
              width: 200,
              ellipsis: true,
              render: (_, record) => (
                <div style={{ lineHeight: 1.5 }}>
                  <div>{record.adset_name || '-'}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{record.adset_id || '-'}</div>
                </div>
              ),
            },
            {
              title: '创意 ID',
              dataIndex: 'creative_id',
              width: 150,
              render: (v) => v || '-',
            },
            {
              title: '类型',
              dataIndex: 'error_type',
              width: 100,
              render: (v) =>
                v ? <Tag color="red">{v}</Tag> : '-',
            },
            {
              title: '错误码',
              dataIndex: 'error_code',
              width: 90,
              render: (v) => v ?? '-',
            },
            {
              title: '摘要',
              dataIndex: 'error_summary',
              ellipsis: true,
              width: 160,
            },
            {
              title: '详情',
              dataIndex: 'error_message',
              ellipsis: true,
            },
          ]}
          locale={{ emptyText: '暂无错误广告' }}
        />
      </Modal>

      <Modal
        title="批量修改公共主页"
        open={pageModalVisible}
        onCancel={() => {
          if (!pageUpdateLoading) {
            setPageModalVisible(false);
          }
        }}
        onOk={handleBatchUpdatePage}
        okText="确认修改"
        confirmLoading={pageUpdateLoading}
        width={520}
      >
        <p style={{ marginBottom: 12 }}>
          将为 <strong>{selectedIssueAdIds.length}</strong> 条广告更换公共主页。
        </p>
        <Form layout="vertical">
          <Form.Item label="选择公共主页" required>
            <Select
              showSearch
              placeholder="选择公共主页"
              loading={pagesLoading}
              value={selectedPageId}
              onChange={setSelectedPageId}
              optionFilterProp="label"
              options={pageList.map((page) => ({
                value: page.page_id || page.id,
                label: `${page.name || '未命名'} (${page.page_id || page.id})`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Campaigns;
